"use client";

import React, { useCallback, useMemo } from 'react';
import {
  GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowProps, GridSortModel, GridTreeNodeWithRender,
} from '@mui/x-data-grid-pro';
import { ConnectionType, ExplorerItem, MediaState, NO_SELECTO_CLASSNAME } from '@/types/types.ts';
import { ExplorerItemType } from '@/enum/explorerItemType.ts';
import styles from './Explorer.module.scss';
import { PlayPauseButton } from "@/common/components/Button/PlayPauseButton.tsx";
import useMediaState from "@/common/hooks/useMediaState.ts";
import { ArrowDropDown, FolderOpen } from "@mui/icons-material";
import AudioIconBars from "@/common/components/AudioIconBars/AudioIconBars.tsx";
import secondsToString from "@/utils/secondsToString.ts";
import { filesize } from "filesize";
import useCurrentExplorerItem from "@/common/hooks/useCurrentExplorerItem.ts";
import { ContextMenuTypes } from "@/enum/contextMenuTypes.ts";
import useCurrentContextMenu from "@/common/hooks/useCurrentContextMenu.ts";
import { User } from "lucide-react";
import { ConnectionInfoPopover } from "@/features/Connection/ConnectionInfoPopover.tsx";
import { createExplorerItemLike, deleteExplorerItemLike } from "@/services/api/explorer-item-like.ts";
import useUniqueTabId from "@/common/hooks/useUniqueTabId.ts";
import { HeartButton } from "@/common/components/Icon/HeartButton.tsx";
import { useMutateExplorerItems } from "@/state/jotai/hooks/useMutateExplorerItems.ts";
import { HandleCell } from './HandleCell';
import { useSearchParamState } from '@/common/hooks/nuqs/useSearchParamState';
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import useExplorerDataGridSortingModel from "@/common/hooks/preferences/useExplorerDataGridSortingModel.ts";
import _, { isArray } from "lodash";
import { IconButton } from "@mui/material";

export const DEFAULT_SORTING_MODEL = [{ field: "sortOrder", sort: "asc" }] as GridSortModel

export const DEFAULT_EXPLORER_DATA_GRID_COLUMN_VISIBILITY_MODEL = {
  manualReorder: false,
  sortOrder: false,
  createdAt: false,
  updatedAt: false,
  createdBy: false,
  duration: false,
  fileSize: false,
  fileType: false,
  fileCreatedAt: false,
}

export const hiddenColumnManagementFields = [
  'id',
  'playBack',
  'name',
  'actions',
]

export const getTogglableColumns = (columns: GridColDef[]) => {
  return columns
    .filter(column => !hiddenColumnManagementFields.includes(column.field))
    .map(column => column.field);
}

export const explorerContentSlotProps = {
  baseButton: {
    sx: {
      color: 'var(--mui-palette-text-primary)',
      '&:hover': {
        color: 'var(--mui-palette-primary-main)',
      },
    },
  },
  columnsManagement: {
    getTogglableColumns,
  },
}

export const ExplorerContentColumns = () => {
  const [mediaState] = useMediaState();
  const [uniqueTabId] = useUniqueTabId();
  const [qSearchParam] = useSearchParamState(URLSearchParamKeys.Q);
  const [explorerDataGridSortingModel] = useExplorerDataGridSortingModel();
  const isDefaultSorting =
    explorerDataGridSortingModel.length === 0 ||
    _.isEqual(explorerDataGridSortingModel, DEFAULT_SORTING_MODEL);

  const currentExplorerItem = useCurrentExplorerItem()
  const [, setCurrentContextMenu] = useCurrentContextMenu();
  const isPlaying = mediaState === MediaState.PLAYING
  const { updateItem } = useMutateExplorerItems();
  const handleTrackDropDownClick = useCallback((mouseEvent: React.MouseEvent, explorerItem: ExplorerItem) => {
    setCurrentContextMenu({
      type: ContextMenuTypes.TRACK,
      data: {
        explorerItem,
        mouseEvent,
      },
    });
  }, [setCurrentContextMenu]);

  const handlePlaylistDropDownClick = useCallback((mouseEvent: React.MouseEvent, playlistId: string, explorerItemId: string) => {
    setCurrentContextMenu({
      type: ContextMenuTypes.PLAYLIST,
      data: {
        playlistId,
        explorerItemId,
        mouseEvent,
      },
    });
  }, [setCurrentContextMenu]);

  const handleDropDownClick = useCallback((e: React.MouseEvent, row: GridRowProps & ExplorerItem) => {
    if (row.type === ExplorerItemType.TRACK) {
      handleTrackDropDownClick(e, row as unknown as ExplorerItem)
    }
    if (row.type === ExplorerItemType.PLAYLIST) {
      handlePlaylistDropDownClick(e, row.playlistId, row.id as string)
    }
  }, [
    handlePlaylistDropDownClick,
    handleTrackDropDownClick
  ])

  const handleLikeClick = useCallback(async (
    event: React.MouseEvent, e: GridRenderCellParams<ExplorerItem, unknown, unknown, GridTreeNodeWithRender>
  ) => {
    event.stopPropagation();

    const currentLiked = e.row.liked;
    const newLiked = !currentLiked;

    try {
      e.api.updateRows([{ id: e.row.id, liked: newLiked }]);

      updateItem({ ...e.row, liked: newLiked });

      if (currentLiked) {
        await deleteExplorerItemLike({
          explorerItem: e.row,
          uniqueTabId,
        });
      } else {
        await createExplorerItemLike({
          explorerItem: e.row,
          uniqueTabId,
        });
      }
    } catch (error) {
      updateItem({ ...e.row, liked: currentLiked });
      console.error('Error updating like status:', error);
    }
  }, [
    uniqueTabId,
    updateItem
  ]);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'playBack',
      headerName: '',
      width: 60,
      pinnable: false,
      sortable: false,
      hideable: false,
      resizable: false,
      align: 'center',
      filterable: false,
      disableReorder: true,
      disableColumnMenu: true,
      cellClassName: styles.FolderOrFileIndicatorIcon,
      renderCell: (params) => {
        if (params.row.type === ExplorerItemType.PLAYLIST) {
          return <IconButton
            onClickCapture={(e) => console.log(e)}
            style={{
              display: "flex",
              top: 2,
            }}
            size={'large'}
          >
            <FolderOpen
              className={NO_SELECTO_CLASSNAME}
            />
          </IconButton>;
        }

        return <PlayPauseButton
          size={'large'}
          explorerItem={params.row}
          style={{
            display: "flex",
            top: 2
          }}
        />;
      },
    },
    {
      field: 'manualReorder',
      headerName: 'Manual reorder',
      sortable: false,
      pinnable: false,
      resizable: false,
      align: 'center',
      maxWidth: 40,
      renderCell: (params) => {
        const disabled = !isDefaultSorting || !!qSearchParam;
        return <HandleCell id={params.row.id.toString()} disabled={disabled} />;
      }
    },
    {
      field: 'sortOrder',
      headerName: 'Index',
      sortable: true,
      pinnable: false,
      resizable: false,
      align: 'center',
      maxWidth: 70,
    },
    {
      flex: 1,
      field: 'name',
      headerName: 'Name',
      editable: true,
      hideable: false,
      pinnable: false,
      renderCell: (params) => {
        return (
          <div style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
          }}>
            {
              isPlaying && params.row.type === ExplorerItemType.PLAYLIST
              && isArray(currentExplorerItem?.parents)
              && currentExplorerItem?.parents.includes(params.id as string)
              && <AudioIconBars style={{
                marginRight: 10,
              }} />
            }

            {params.value}
          </div>
        );
      },
    },
    {
      field: 'liked',
      headerName: 'Liked',
      renderHeader: () => null,
      width: 60,
      sortable: true,
      pinnable: false,
      filterable: true,
      hideable: true,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (e) => {
        if (e.row.type === ExplorerItemType.PLAYLIST) {
          return;
        }

        return (
          <HeartButton
            isActive={e.row.liked}
            onClick={
              event => handleLikeClick(event, e)
            }
          />
        );
      }
    },
    {
      field: 'duration',
      headerName: 'Duration',
      align: "right",
      headerAlign: "right",
      pinnable: false,
      maxWidth: 100,
      renderCell: (params) => params.row.type === ExplorerItemType.TRACK ? secondsToString(params.value) : ''
    },
    {
      field: 'fileSize',
      headerName: 'Size',
      display: "flex",
      pinnable: false,
      maxWidth: 100,
      renderCell: (params) => params.row.type === ExplorerItemType.TRACK ? filesize(params.value) : ''
    },
    { field: 'fileType', headerName: 'Type', pinnable: false, maxWidth: 70, align: 'right' },
    { field: 'createdAt', headerName: 'Created', pinnable: false, width: 140, align: 'right' },
    { field: 'updatedAt', headerName: 'Updated', pinnable: false, width: 140, align: 'right' },
    { field: 'fileCreatedAt', headerName: 'File created', pinnable: false, width: 140, align: 'right' },
    {
      field: 'createdBy',
      headerName: 'Created by',
      pinnable: false,
      maxWidth: 400,
      flex: 1,
      renderCell: (params) => {
        return (
          <ConnectionInfoPopover
            debounceFetchTime={300}
            connectionId={params.row.createdBy?.id}
            connectionType={ConnectionType.USER}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <User size={16} />
              {params.row.createdBy?.fullName || ''}
            </div>
          </ConnectionInfoPopover>
        )
      }
    },
    {
      display: 'flex',
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 50,
      resizable: false,
      pinnable: false,
      sortable: false,
      disableReorder: true,
      cellClassName: 'actions',
      getActions: (params) => {
        const { row } = params;
        return [
          <GridActionsCellItem
            key="Options"
            label="Options"
            icon={<ArrowDropDown />}
            onClick={(e: React.MouseEvent) => {
              handleDropDownClick(e, row)
            }}
            onClickCapture={(e: React.MouseEvent) => {
              handleDropDownClick(e, row)
            }}
          />
        ];
      },
    },
  ], [
    isDefaultSorting,
    qSearchParam,
    isPlaying,
    currentExplorerItem?.parents,
    handleLikeClick,
    handleDropDownClick
  ]);

  return columns;
};
